import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Button,
  Grid,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { toast } from 'react-toastify';
import { API } from 'utilities/api';
import { Code, EmptyState, KpiCard, LoadingOverlay } from 'dashboard/components/common';

/**
 * Period tax report. Defaults to current year on mount so an owner can
 * land on real numbers without filling in dates.
 */
export default function ReportsTab({ brandId }) {
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);

  const run = useCallback(() => {
    if (!brandId) return;
    setLoading(true);
    API.getTaxReport({ brand_id: brandId, from: from || undefined, to: to || undefined })
      .then(setReport)
      .catch((err) => toast.error(err?.message || 'Failed'))
      .finally(() => setLoading(false));
  }, [brandId, from, to]);

  useEffect(() => {
    if (!from && !to) {
      const now = new Date();
      const start = new Date(Date.UTC(now.getUTCFullYear(), 0, 1)).toISOString().slice(0, 10);
      const end = new Date(Date.UTC(now.getUTCFullYear() + 1, 0, 1)).toISOString().slice(0, 10);
      setFrom(start); setTo(end);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!from || !to || !brandId) return undefined;
    let cancelled = false;
    setLoading(true);
    API.getTaxReport({ brand_id: brandId, from, to })
      .then((res) => { if (!cancelled) setReport(res); })
      .catch((err) => { if (!cancelled) toast.error(err?.message || 'Failed', { toastId: 'taxreport-load-error' }); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [from, to, brandId]);

  return (
    <Box>
      <Stack direction='row' justifyContent='space-between' alignItems='flex-end' flexWrap='wrap' gap='10px' mb='14px'>
        <Box>
          <Typography fontWeight='700'>Tax report</Typography>
          <Typography variant='body2' color='text.secondary'>
            Tax collected per jurisdiction × tax label, for the selected period.
          </Typography>
        </Box>
        <Stack direction='row' spacing='8px' alignItems='flex-end' flexWrap='wrap'>
          <TextField
            size='small'
            type='date'
            label='From'
            value={from}
            onChange={(e) => setFrom(e.target.value)}
            sx={{ maxWidth: 160 }}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            size='small'
            type='date'
            label='To'
            value={to}
            onChange={(e) => setTo(e.target.value)}
            sx={{ maxWidth: 160 }}
            InputLabelProps={{ shrink: true }}
          />
          <Button onClick={run} disabled={loading}>
            {loading ? 'Loading…' : 'Refresh'}
          </Button>
        </Stack>
      </Stack>

      {report && (
        <Grid container spacing='12px' mb='16px'>
          <Grid item xs={12} md={4}>
            <KpiCard label='Invoices' value={report.invoice_count} />
          </Grid>
          <Grid item xs={12} md={4}>
            <KpiCard
              label='Revenue (excl. tax)'
              value={report.total_revenue.toFixed(2)}
              hint='across all currencies as recorded'
              accent='#16a34a'
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <KpiCard
              label='Tax collected'
              value={report.total_tax.toFixed(2)}
              accent='#2563eb'
            />
          </Grid>
        </Grid>
      )}

      <LoadingOverlay loading={loading && !report} minHeight={180}>
      <TableContainer
        component={Box}
        sx={{ border: '1px solid', borderColor: 'divider', borderRadius: '12px', overflowX: 'auto' }}
      >
        <Table size='small'>
          <TableHead sx={{ bgcolor: 'action.hover' }}>
            <TableRow>
              <TableCell>Country</TableCell>
              <TableCell>Region</TableCell>
              <TableCell>Tax</TableCell>
              <TableCell align='right'>Rate</TableCell>
              <TableCell align='right'>Invoices</TableCell>
              <TableCell align='right'>Amount</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {!report && (
              <TableRow>
                <TableCell colSpan={6}>
                  <EmptyState>Pick a date range to run a report.</EmptyState>
                </TableCell>
              </TableRow>
            )}
            {report && (report.breakdown || []).length === 0 && (
              <TableRow>
                <TableCell colSpan={6}>
                  <EmptyState>No taxable invoices in this period.</EmptyState>
                </TableCell>
              </TableRow>
            )}
            {report && (report.breakdown || []).map((r, i) => (
              <TableRow key={i}>
                <TableCell><Code>{r.country}</Code></TableCell>
                <TableCell><Code>{r.region}</Code></TableCell>
                <TableCell>{r.name}</TableCell>
                <TableCell align='right'>{(r.rate_basis_points / 100).toFixed(2)}%</TableCell>
                <TableCell align='right'>{r.invoice_count}</TableCell>
                <TableCell align='right'>{r.tax_amount.toFixed(2)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      </LoadingOverlay>
    </Box>
  );
}
